import { PROJECT_MESSAGES } from "../../messages";
import type { RunOutput, WorkspaceType } from "../../projectTypes";

const MAX_ROWS = 200;

const PLACEHOLDERS: Record<WorkspaceType, string> = {
    program: PROJECT_MESSAGES.outputPlaceholder,
    web: PROJECT_MESSAGES.webPlaceholder,
    sql: PROJECT_MESSAGES.sqlPlaceholder
};

const formatCell = (value: unknown): string => {
    if (value === null || value === undefined) return "NULL";
    return typeof value === "object" ? JSON.stringify(value) : String(value);
};

interface RunResultProps {
    type: WorkspaceType;
    output: RunOutput;
    running: boolean;
    hasRun?: boolean; // Whether something was run: web shows no placeholder before that
}

// What a run produced: text (program output, errors) and, for SQL, a table of rows
const RunResult = ({ type, output, running, hasRun = true }: RunResultProps) => {
    if (running) return <pre className="whitespace-pre-wrap">{PROJECT_MESSAGES.running}</pre>;

    const { text, table } = output;
    const nothing = !text && !table;

    if (nothing && type === "web" && !hasRun) return null;

    return (
        <div className="space-y-4">
            {text && <pre className="whitespace-pre-wrap">{text}</pre>}

            {table && (
                <div className="overflow-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                        <tr>
                            {table.columns.map((column) => (
                                <th key={column} className="border border-gray-700 px-3 py-2 text-start">
                                    {column}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {table.rows.slice(0, MAX_ROWS).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {table.columns.map((column, cellIndex) => (
                                    <td key={column} className="border border-gray-800 px-3 py-2">
                                        {formatCell(row[cellIndex])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {table.rows.length === 0 && (
                        <p className="mt-2 text-gray-400">{PROJECT_MESSAGES.emptyTable}</p>
                    )}
                    {table.rows.length > MAX_ROWS && (
                        <p className="mt-2 text-gray-400">
                            {PROJECT_MESSAGES.rowsLimited(MAX_ROWS, table.rows.length)}
                        </p>
                    )}
                </div>
            )}

            {nothing && <pre className="whitespace-pre-wrap text-gray-400">{PLACEHOLDERS[type]}</pre>}
        </div>
    );
};

export default RunResult;